import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getShops,
  getInvoices,
  getActivity,
  getAllDeals,
  downloadCsv,
} from '../../services/adminMockData';
import { getPlatformStats } from '../../services/apiService';

function startOfTodayIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function daysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min}m geleden`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}u geleden`;
  return `${Math.floor(hr / 24)}d geleden`;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState(() => getShops());
  const [invoices, setInvoices] = useState(() => getInvoices());
  const [activity, setActivity] = useState(() => getActivity());
  const [deals, setDeals] = useState(() => getAllDeals());
  const [platformStats, setPlatformStats] = useState(null);

  useEffect(() => {
    const refresh = () => {
      setShops(getShops());
      setInvoices(getInvoices());
      setActivity(getActivity());
      setDeals(getAllDeals());
    };
    window.addEventListener('walkin-admin-shops-change', refresh);
    window.addEventListener('walkin-deals-change', refresh);
    return () => {
      window.removeEventListener('walkin-admin-shops-change', refresh);
      window.removeEventListener('walkin-deals-change', refresh);
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getPlatformStats();
        if (active) setPlatformStats(data);
      } catch {
        if (active) setPlatformStats(null);
      }
    })();
    return () => { active = false; };
  }, []);

  // Headline metrics from the platform stats API (numeric fields are strings).
  const platform = useMemo(() => {
    const s = platformStats || {};
    return {
      totalRetailers: Number(s.totalRetailers || 0),
      activeRetailers: Number(s.activeRetailers || 0),
      activeDeals: Number(s.activeDeals || 0),
      totalDeals: Number(s.totalDeals || 0),
      totalRedemptions: Number(s.totalRedemptions || 0),
      totalWalkers: Number(s.totalWalkers || 0),
      totalViews: Number(s.totalViews || 0),
      activeAreas: Number(s.activeAreas || 0),
      recentRedemptions: Array.isArray(s.recentRedemptions) ? s.recentRedemptions : [],
    };
  }, [platformStats]);

  const stats = useMemo(() => {
    const todayStart = startOfTodayIso();
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const weekStart = daysAgo(7);
    const activeDeals = deals.filter((d) => d.status === 'actief');
    const dealsToday = deals.filter((d) => d.createdAt && d.createdAt >= todayStart);
    const tokensSold = shops.reduce((sum, s) => sum + (s.tokensPurchased || 0), 0);
    const revenueThisMonth = invoices
      .filter((inv) => new Date(inv.date) >= monthStart)
      .reduce((sum, inv) => sum + inv.amount, 0);
    const newRegistrations = shops.filter(
      (s) => new Date(s.createdAt) >= weekStart
    ).length;
    return {
      totalShops: shops.length,
      activeDeals: activeDeals.length,
      dealsToday: dealsToday.length,
      tokensSold,
      revenueThisMonth,
      newRegistrations,
    };
  }, [shops, deals, invoices]);

  const recentDeals = useMemo(() => {
    return [...deals]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 10);
  }, [deals]);

  const recentShops = useMemo(() => {
    return [...shops]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 5);
  }, [shops]);

  const recentInvoices = useMemo(() => invoices.slice(0, 5), [invoices]);

  const handleExportInvoices = () => {
    downloadCsv(
      `walkin-facturen-${new Date().toISOString().slice(0, 10)}.csv`,
      invoices.map((inv) => ({
        nummer: inv.number,
        winkel: inv.shopName,
        type: inv.type,
        bedrag_ex: inv.amount,
        btw: inv.vat,
        totaal: inv.total,
        datum: inv.date.slice(0, 10),
        status: inv.status,
      }))
    );
  };

  return (
    <div>
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-sub">Overzicht van het WalkIn platform</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="admin-btn admin-btn--ghost" onClick={() => navigate('/admin/shops')}>
            Nieuwe winkelier
          </button>
          <button className="admin-btn admin-btn--ghost" onClick={() => navigate('/admin/areas/new')}>
            Nieuw gebied
          </button>
          <button className="admin-btn admin-btn--orange" onClick={handleExportInvoices}>
            Export facturen
          </button>
        </div>
      </header>

      <div className="admin-grid" style={{ marginBottom: 22 }}>
        <div className="admin-stat">
          <span className="admin-stat__label">Totaal winkeliers</span>
          <span className="admin-stat__value">{platform.totalRetailers}</span>
          <span className="admin-stat__delta">{platform.activeRetailers} actief</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Actieve deals</span>
          <span className="admin-stat__value">{platform.activeDeals}</span>
          <span className="admin-stat__delta">{platform.totalDeals} totaal</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Inleveringen</span>
          <span className="admin-stat__value">{platform.totalRedemptions.toLocaleString('nl-NL')}</span>
          <span className="admin-stat__delta">totaal</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Walkers</span>
          <span className="admin-stat__value">{platform.totalWalkers.toLocaleString('nl-NL')}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Deal weergaven</span>
          <span className="admin-stat__value">{platform.totalViews.toLocaleString('nl-NL')}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Actieve gebieden</span>
          <span className="admin-stat__value">{platform.activeAreas}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Tokens verkocht</span>
          <span className="admin-stat__value">{stats.tokensSold}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Omzet deze maand</span>
          <span className="admin-stat__value">€{stats.revenueThisMonth.toLocaleString('nl-NL')}</span>
          <span className="admin-stat__delta">ex BTW</span>
        </div>
      </div>

      {platform.recentRedemptions.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__title">Laatste inleveringen</div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Deal</th>
                  <th>Winkelier</th>
                  <th>Ingeleverd</th>
                </tr>
              </thead>
              <tbody>
                {platform.recentRedemptions.map((r, i) => (
                  <tr key={i}>
                    <td>{r.dealTitle || '—'}</td>
                    <td>{r.retailerName || '—'}</td>
                    <td>{r.redeemedAt ? timeAgo(r.redeemedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card__title">
          Laatste deals
          <button className="admin-link" onClick={() => navigate('/admin/deals')}>
            Alles bekijken →
          </button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Deal</th>
                <th>Winkelier</th>
                <th>Gebied</th>
                <th>Status</th>
                <th>Aangemaakt</th>
              </tr>
            </thead>
            <tbody>
              {recentDeals.length === 0 && (
                <tr><td colSpan={5} className="admin-empty">Nog geen deals</td></tr>
              )}
              {recentDeals.map((d) => (
                <tr key={d.id}>
                  <td>{d.emoji} {d.title}</td>
                  <td>{d.shopName}</td>
                  <td>{d.area}</td>
                  <td>
                    <span className={`admin-badge admin-badge--${d.status || 'actief'}`}>
                      {d.status || 'actief'}
                    </span>
                  </td>
                  <td>{timeAgo(d.createdAt || new Date().toISOString())}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__title">
          Nieuwste winkeliers
          <button className="admin-link" onClick={() => navigate('/admin/shops')}>
            Alles bekijken →
          </button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Winkelnaam</th>
                <th>Gebied</th>
                <th>Status</th>
                <th>Lid sinds</th>
              </tr>
            </thead>
            <tbody>
              {recentShops.map((s) => (
                <tr key={s.id}>
                  <td>
                    <button
                      className="admin-link"
                      onClick={() => navigate(`/admin/shops/${s.id}`)}
                    >
                      {s.shopName}
                    </button>
                  </td>
                  <td>{s.area}</td>
                  <td>
                    <span className={`admin-badge admin-badge--${s.status}`}>
                      {s.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{formatDate(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__title">
          Laatste token aankopen
          <button className="admin-link" onClick={() => navigate('/admin/invoices')}>
            Alle facturen →
          </button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Factuur</th>
                <th>Winkel</th>
                <th>Pakket</th>
                <th>Totaal</th>
                <th>Datum</th>
              </tr>
            </thead>
            <tbody>
              {recentInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td><span className="admin-mono">{inv.number}</span></td>
                  <td>{inv.shopName}</td>
                  <td>
                    <span className={`admin-badge admin-badge--${inv.type.toLowerCase()}`}>
                      {inv.type}
                    </span>
                  </td>
                  <td>€{inv.total.toFixed(2)}</td>
                  <td>{formatDate(inv.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {activity.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__title">Recente activiteit</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {activity.slice(0, 8).map((a) => (
              <li key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid #F1F2F4', fontSize: 13 }}>
                <strong>{a.admin}</strong> · {a.action}
                <span style={{ float: 'right', color: '#9CA3AF', fontSize: 12 }}>
                  {timeAgo(a.at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
